import type {
  ManagementSession,
  ManagementUser,
  SubmissionDetail,
  SubmissionStats,
  SubmissionSummary,
} from '@mapeamento/shared';
import { byId, esc } from './lib/html';
import {
  ApiRequestError,
  createManagementUser,
  deleteManagementUser,
  downloadSubmissionsCsv,
  getSubmissionDetail,
  getSubmissionStats,
  listManagementUsers,
  listSubmissions,
  loginManagement,
  updateManagementUser,
} from './services/api-client';

const SESSION_KEY = 'poa-ia156-management-session-v1';
const PAGE_SIZE = 12;

interface ManagementState {
  session: ManagementSession | null;
  view: 'dashboard' | 'submissions' | 'consolidated' | 'access';
  q: string;
  page: number;
  stats: SubmissionStats | null;
  submissions: SubmissionSummary[];
  total: number;
  detail: SubmissionDetail | null;
  users: ManagementUser[];
  editingUserId: string | null;
  loading: boolean;
  message: string;
}

const fmtDate = (value: string | null) =>
  value ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-';

const fmtInt = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

const fmtDecimal = (value: number) =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(value);

const pct = (part: number, total: number) => (total ? Math.max(4, (part / total) * 100) : 0);

export class ManagementApp {
  private readonly $root = byId('app');
  private state: ManagementState = {
    session: this.loadSession(),
    view: 'dashboard',
    q: '',
    page: 1,
    stats: null,
    submissions: [],
    total: 0,
    detail: null,
    users: [],
    editingUserId: null,
    loading: false,
    message: '',
  };

  start(): void {
    document.body.classList.add('management-page');
    byId('pg').textContent = 'Gestão';
    document.querySelector<HTMLElement>('#top .lbl')!.textContent = 'Painel administrativo';
    this.$root.addEventListener('click', (event) => void this.onClick(event));
    this.$root.addEventListener('submit', (event) => void this.onSubmit(event));
    this.$root.addEventListener('input', (event) => this.onInput(event));
    this.render();
    if (this.state.session) void this.refresh();
  }

  private render(): void {
    if (!this.state.session) {
      this.$root.innerHTML = this.renderLogin();
      return;
    }

    this.$root.innerHTML = `
      <div class="mg-shell">
        <aside class="mg-side">
          <div>
            <p class="eyebrow">Management</p>
            <h1>Painel de gestão</h1>
            <p>${esc(this.state.session.user.nome)} · ${this.state.session.user.role}</p>
          </div>
          <nav>
            ${this.navButton('dashboard', 'Métricas')}
            ${this.navButton('submissions', 'Formulários')}
            ${this.navButton('consolidated', 'Dados consolidados')}
            ${this.navButton('access', 'Gestão de acessos')}
          </nav>
          <button class="btn" data-action="logout" type="button">Sair</button>
        </aside>
        <section class="mg-main">
          <div class="mg-head">
            <div>
              <p class="eyebrow">Central do Cidadão 156</p>
              <h2>${this.title()}</h2>
            </div>
            <button class="btn solid" data-action="export" type="button">Exportar Excel</button>
          </div>
          ${this.state.message ? `<div class="alert">${esc(this.state.message)}</div>` : ''}
          ${this.renderView()}
        </section>
      </div>`;
  }

  private renderLogin(): string {
    return `
      <section class="mg-login">
        <form class="panel mg-login-card" data-form="login">
          <p class="eyebrow">Área protegida</p>
          <h1>Entrar no management</h1>
          <p>Use seu usuário cadastrado para acessar o painel.</p>
          <label class="f">
            <span>E-mail</span>
            <input name="email" type="email" autocomplete="username" placeholder="admin@poa.digital" />
          </label>
          <label class="f">
            <span>Senha</span>
            <input name="password" type="password" autocomplete="current-password" required />
          </label>
          ${this.state.message ? `<div class="alert">${esc(this.state.message)}</div>` : ''}
          <button class="btn solid" type="submit">Entrar</button>
        </form>
      </section>`;
  }

  private renderView(): string {
    if (this.state.loading) return '<div class="panel muted">Carregando...</div>';
    if (this.state.view === 'dashboard') return this.renderDashboard();
    if (this.state.view === 'submissions') return this.renderSubmissions();
    if (this.state.view === 'consolidated') return this.renderConsolidated();
    return this.renderAccess();
  }

  private renderDashboard(): string {
    const stats = this.state.stats;
    if (!stats) return '<div class="panel muted">Sem métricas carregadas.</div>';
    return `
      <div class="mg-kpis">
        ${this.kpi('Formulários', stats.totalSubmissions)}
        ${this.kpi('Fontes mapeadas', stats.totalSources)}
        ${this.kpi('Anexos', stats.totalAttachments)}
        ${this.kpi('Últimos 7 dias', stats.submissionsLast7Days)}
      </div>
      <div class="mg-grid">
        <section class="mg-card">
          <h3>Envios por secretaria</h3>
          ${this.bars(
            stats.bySecretaria.slice(0, 8),
            stats.totalSubmissions,
            (i) => `${esc(i.sigla ?? i.secretaria)} <b>${fmtInt(i.total)}</b>`,
          )}
        </section>
        <section class="mg-card">
          <h3>Tipos de fonte</h3>
          ${this.bars(
            stats.bySourceType.slice(0, 8),
            stats.totalSources,
            (i) => `${esc(i.tipo)} <b>${fmtInt(i.total)}</b>`,
          )}
        </section>
      </div>`;
  }

  private renderSubmissions(): string {
    return `
      <div class="mg-toolbar">
        <input data-field="q" type="search" value="${esc(this.state.q)}" placeholder="Buscar secretaria, sigla ou protocolo" />
        <button class="btn" data-action="search" type="button">Buscar</button>
      </div>
      <div class="mg-table">
        ${this.state.submissions
          .map(
            (item) => `
          <button class="mg-row" data-open="${item.id}" type="button">
            <span><b>${esc(item.protocolo)}</b><small>${esc(item.secretaria)}</small></span>
            <span>${fmtInt(item.totalFontes)} fontes</span>
            <span>${fmtDate(item.createdAt)}</span>
          </button>`,
          )
          .join('')}
      </div>
      <div class="mg-pager">
        <button class="btn" data-action="prev" type="button" ${this.state.page <= 1 ? 'disabled' : ''}>Anterior</button>
        <span>${fmtInt(this.state.page)} / ${fmtInt(Math.max(1, Math.ceil(this.state.total / PAGE_SIZE)))}</span>
        <button class="btn" data-action="next" type="button" ${this.state.page * PAGE_SIZE >= this.state.total ? 'disabled' : ''}>Próxima</button>
      </div>
      ${this.state.detail ? this.renderDetail(this.state.detail) : ''}`;
  }

  private renderConsolidated(): string {
    const stats = this.state.stats;
    if (!stats) return '<div class="panel muted">Sem dados consolidados.</div>';
    return `
      <div class="mg-grid">
        <section class="mg-card">
          <h3>Resumo consolidado</h3>
          <dl class="mg-dl">
            <dt>Média de fontes por formulário</dt><dd>${fmtDecimal(stats.averageSourcesPerSubmission)}</dd>
            <dt>Último envio</dt><dd>${fmtDate(stats.lastSubmissionAt)}</dd>
            <dt>Total de secretarias respondentes</dt><dd>${fmtInt(stats.bySecretaria.length)}</dd>
          </dl>
        </section>
        <section class="mg-card">
          <h3>Níveis de acesso</h3>
          ${this.bars(
            stats.byAccessLevel,
            stats.totalSources,
            (i) => `${esc(i.acesso)} <b>${fmtInt(i.total)}</b>`,
          )}
        </section>
      </div>
      <section class="mg-card">
        <h3>Secretarias</h3>
        <div class="mg-table static">
          ${stats.bySecretaria
            .map(
              (item) => `
            <div class="mg-row">
              <span><b>${esc(item.secretaria)}</b><small>${esc(item.sigla ?? 'Sem sigla')}</small></span>
              <span>${fmtInt(item.total)} formulários</span>
              <span>${fmtInt(item.fontes)} fontes</span>
            </div>`,
            )
            .join('')}
        </div>
      </section>`;
  }

  private renderAccess(): string {
    const isAdmin = this.state.session?.user.role === 'admin';
    return `
      <div class="mg-grid">
        <form class="mg-card" data-form="create-user">
          <h3>Criar acesso</h3>
          ${isAdmin ? '' : '<p class="muted">Seu usuário pode visualizar dados, mas não criar acessos.</p>'}
          <input name="nome" type="text" placeholder="Nome" ${isAdmin ? '' : 'disabled'} required />
          <input name="email" type="email" placeholder="E-mail" ${isAdmin ? '' : 'disabled'} required />
          <input name="password" type="password" placeholder="Senha inicial" ${isAdmin ? '' : 'disabled'} required minlength="8" />
          <button class="btn solid" type="submit" ${isAdmin ? '' : 'disabled'}>Criar acesso</button>
        </form>
        <section class="mg-card">
          <h3>Configurações de login</h3>
          <p class="muted">O formulário público não exige login. Somente o /management usa autenticação por e-mail e senha.</p>
          <p class="muted">O administrador fixo é admin@poadigital.com.br. Ele não pode ser excluído; apenas a senha pode ser alterada.</p>
        </section>
      </div>
      <section class="mg-card">
        <h3>Usuários</h3>
        <div class="mg-table static">
          ${this.state.users.map((user) => this.renderUserRow(user, isAdmin)).join('')}
        </div>
      </section>`;
  }

  private renderUserRow(user: ManagementUser, isAdmin: boolean): string {
    const fixed = user.email === 'admin@poadigital.com.br';
    if (this.state.editingUserId === user.id) {
      return `
        <form class="mg-user-edit" data-form="edit-user" data-user-id="${user.id}">
          <div class="mg-edit-grid">
            <input name="nome" type="text" value="${esc(user.nome)}" placeholder="Nome" ${fixed ? 'disabled' : ''} />
            <input name="email" type="email" value="${esc(user.email)}" placeholder="E-mail" ${fixed ? 'disabled' : ''} />
            <input name="password" type="password" placeholder="${fixed ? 'Nova senha do administrador' : 'Nova senha (opcional)'}" />
          </div>
          <div class="mg-actions">
            <button class="btn solid" type="submit">Salvar</button>
            <button class="btn" data-action="cancel-edit" type="button">Cancelar</button>
          </div>
        </form>`;
    }

    return `
      <div class="mg-row mg-user-row">
        <span>
          <b>${esc(user.nome)}</b>
          <small>${esc(user.email)}${fixed ? ' · administrador fixo' : ''}</small>
        </span>
        <span>${user.role === 'admin' ? 'Administrador' : 'Usuário'}</span>
        <span class="mg-actions">
          <button class="btn" data-edit-user="${user.id}" type="button" ${isAdmin ? '' : 'disabled'}>Editar</button>
          ${
            fixed
              ? ''
              : `<button class="btn danger" data-delete-user="${user.id}" type="button" ${isAdmin ? '' : 'disabled'}>Excluir</button>`
          }
        </span>
      </div>`;
  }

  private renderDetail(detail: SubmissionDetail): string {
    return `
      <section class="mg-card mg-detail">
        <div class="mg-head compact">
          <div>
            <h3>${esc(detail.protocolo)} · ${esc(detail.secretaria.nome)}</h3>
            <p>${fmtDate(detail.createdAt)} · ${esc(detail.secretaria.responsavel)} · ${esc(detail.secretaria.email)}</p>
          </div>
          <button class="btn" data-action="close-detail" type="button">Fechar</button>
        </div>
        <h4>Fontes</h4>
        ${detail.fontes
          .map(
            (fonte) => `
          <div class="mg-source">
            <b>${fonte.position}. ${esc(fonte.nome)}</b>
            <span>${esc(fonte.tipo)} · ${esc(fonte.atualizada)} · ${esc(fonte.acesso ?? 'Sem acesso informado')}</span>
            <p>${esc(fonte.assuntos)}</p>
            <small>${esc(fonte.onde)}</small>
          </div>`,
          )
          .join('')}
        <h4>Pontos focais</h4>
        ${detail.pontosFocais
          .map(
            (p) =>
              `<p class="muted">${esc(p.papel)}: ${esc(p.nome)} · ${esc(p.email)} · ${esc(p.whatsapp)}</p>`,
          )
          .join('')}
      </section>`;
  }

  private navButton(view: ManagementState['view'], label: string): string {
    return `<button class="${this.state.view === view ? 'active' : ''}" data-view="${view}" type="button">${label}</button>`;
  }

  private title(): string {
    return {
      dashboard: 'Métricas',
      submissions: 'Formulários preenchidos',
      consolidated: 'Dados completos e consolidados',
      access: 'Gestão de acessos',
    }[this.state.view];
  }

  private kpi(label: string, value: number): string {
    return `<div class="mg-kpi"><span>${esc(label)}</span><b>${fmtInt(value)}</b></div>`;
  }

  private bars<T extends { total: number }>(items: T[], total: number, label: (item: T) => string) {
    return `<div class="mg-bars">${items
      .map(
        (item) => `
        <div>
          <p>${label(item)}</p>
          <i style="width:${pct(item.total, total)}%"></i>
        </div>`,
      )
      .join('')}</div>`;
  }

  private async refresh(): Promise<void> {
    if (!this.state.session) return;
    this.state.loading = true;
    this.render();
    try {
      const token = this.state.session.token;
      const [statsResult, submissionsResult] = await Promise.allSettled([
        getSubmissionStats(token),
        listSubmissions(token, { q: this.state.q, page: this.state.page, pageSize: PAGE_SIZE }),
      ]);
      if (submissionsResult.status === 'rejected') throw submissionsResult.reason;
      const submissions = submissionsResult.value;

      if (statsResult.status === 'fulfilled') {
        this.state.stats = statsResult.value;
        this.state.message = '';
      } else {
        this.state.stats = null;
        this.state.message =
          'Formulários carregados, mas as métricas consolidadas não responderam.';
      }

      this.state.submissions = submissions.items;
      this.state.total = submissions.total;
    } catch (error) {
      this.handleError(error);
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  private async loadUsers(): Promise<void> {
    if (!this.state.session || this.state.session.user.role !== 'admin') return;
    try {
      this.state.users = await listManagementUsers(this.state.session.token);
      this.state.message = '';
    } catch (error) {
      this.handleError(error);
    } finally {
      this.render();
    }
  }

  private async onSubmit(event: Event): Promise<void> {
    const form = event.target as HTMLFormElement;
    if (!form.matches('form')) return;
    event.preventDefault();
    const data = new FormData(form);
    try {
      if (form.dataset.form === 'login') {
        this.state.message = '';
        const session = await loginManagement(
          String(data.get('email') || 'admin-token@local'),
          String(data.get('password') || ''),
        );
        this.state.session = session;
        this.saveSession(session);
        await this.refresh();
      } else if (form.dataset.form === 'create-user' && this.state.session) {
        await createManagementUser(this.state.session.token, {
          nome: String(data.get('nome') || ''),
          email: String(data.get('email') || ''),
          password: String(data.get('password') || ''),
        });
        form.reset();
        this.state.message = 'Acesso criado com sucesso.';
        await this.loadUsers();
      } else if (form.dataset.form === 'edit-user' && this.state.session) {
        const password = String(data.get('password') || '');
        const user = this.state.users.find((item) => item.id === form.dataset.userId);
        const fixed = user?.email === 'admin@poadigital.com.br';
        await updateManagementUser(
          this.state.session.token,
          form.dataset.userId!,
          fixed
            ? { ...(password ? { password } : {}) }
            : {
                nome: String(data.get('nome') || ''),
                email: String(data.get('email') || ''),
                ...(password ? { password } : {}),
              },
        );
        this.state.editingUserId = null;
        this.state.message = 'Usuário atualizado com sucesso.';
        await this.loadUsers();
      }
    } catch (error) {
      if (
        form.dataset.form === 'login' &&
        error instanceof ApiRequestError &&
        error.status === 401
      ) {
        this.state.message = 'E-mail ou senha inválidos.';
      } else {
        this.handleError(error);
      }
      this.render();
    }
  }

  private async onClick(event: Event): Promise<void> {
    const target = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-view],[data-action],[data-open],[data-edit-user],[data-delete-user]',
    );
    if (!target) return;

    try {
      const view = target.dataset.view as ManagementState['view'] | undefined;
      if (view) {
        this.state.view = view;
        this.state.detail = null;
        this.render();
        if (view === 'access') await this.loadUsers();
        return;
      }

      const action = target.dataset.action;
      if (action === 'logout') return this.logout();
      if (action === 'export' && this.state.session)
        return downloadSubmissionsCsv(this.state.session.token);
      if (action === 'search') {
        this.state.page = 1;
        return this.refresh();
      }
      if (action === 'prev') {
        this.state.page = Math.max(1, this.state.page - 1);
        return this.refresh();
      }
      if (action === 'next') {
        this.state.page += 1;
        return this.refresh();
      }
      if (action === 'close-detail') {
        this.state.detail = null;
        this.render();
        return;
      }
      if (action === 'cancel-edit') {
        this.state.editingUserId = null;
        this.render();
        return;
      }
      if (target.dataset.open && this.state.session) {
        this.state.detail = await getSubmissionDetail(
          this.state.session.token,
          target.dataset.open,
        );
        this.render();
        return;
      }
      if (target.dataset.editUser) {
        this.state.editingUserId = target.dataset.editUser;
        this.render();
        return;
      }
      if (target.dataset.deleteUser && this.state.session) {
        if (!confirm('Excluir este usuário?')) return;
        await deleteManagementUser(this.state.session.token, target.dataset.deleteUser);
        this.state.message = 'Usuário excluído com sucesso.';
        await this.loadUsers();
      }
    } catch (error) {
      this.handleError(error);
      this.render();
    }
  }

  private onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.dataset.field === 'q') this.state.q = input.value;
  }

  private handleError(error: unknown): void {
    this.state.message =
      error instanceof ApiRequestError || error instanceof Error
        ? error.message
        : 'Não foi possível concluir a ação.';
    if (error instanceof ApiRequestError && error.status === 401) this.logout(false);
  }

  private loadSession(): ManagementSession | null {
    try {
      return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null') as ManagementSession | null;
    } catch {
      return null;
    }
  }

  private saveSession(session: ManagementSession): void {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  private logout(render = true): void {
    sessionStorage.removeItem(SESSION_KEY);
    this.state.session = null;
    this.state.message = render ? '' : 'Sessão expirada. Entre novamente.';
    if (render) this.render();
  }
}
